import { isNullish } from '../';
import type { Entries, Nullish } from '../../types/utility';

const EN_US = 'en-US';

const CURRENCY_OPTIONS = {
  ToNearestCent: { style: 'currency', currency: 'USD' },
  ToNearestNickel: { style: 'currency', currency: 'USD', roundingIncrement: 5 },
  ToNearestDime: { style: 'currency', currency: 'USD', roundingIncrement: 10 },
  ToNearestQuarter: { style: 'currency', currency: 'USD', roundingIncrement: 25 },
  ToNearestHalfDollar: { style: 'currency', currency: 'USD', roundingIncrement: 50 },
  ToNearestDollar: { style: 'currency', currency: 'USD', roundingIncrement: 100 },
};

const CURRENCY_FORMATTERS = (
  Object.entries(CURRENCY_OPTIONS) as Entries<typeof CURRENCY_OPTIONS>
).reduce(
  (formats, [format, options]) => {
    return {
      ...formats,
      [format]: new Intl.NumberFormat(EN_US, options as Intl.NumberFormatOptions),
    };
  },
  {} as Record<keyof typeof CURRENCY_OPTIONS, Intl.NumberFormat>,
);

/*
  TODO: the `formatAbbreviated` function below can be simplified to use Intl.NumberFormat with the `notation: 'compact'` option
  (1000000).toLocaleString('en-US', { notation: 'compact', compactDisplay: 'short' }) -> '1M'
  (105000).toLocaleString('en-US', { notation: 'compact', compactDisplay: 'short' }) -> '105K'
*/
/** Formats a number as an abbreviated currency string (e.g. "$1.5M", "$850K", "$95,000") */
function formatAbbreviated(value: number): string {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1_000_000) {
    const millions = absValue / 1_000_000;
    const formatted = millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1);
    // Strip trailing ".0" that toFixed(1) might produce for whole numbers
    const cleaned = formatted.replace(/\.0$/, '');
    return `${sign}$${cleaned}M`;
  }

  if (absValue >= 100_000) {
    const thousands = absValue / 1_000;
    const formatted = thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1);
    const cleaned = formatted.replace(/\.0$/, '');
    return `${sign}$${cleaned}K`;
  }

  return CURRENCY_FORMATTERS.ToNearestDollar.format(value).split('.')[0] || '';
}

type CurrencyFormatOption = keyof typeof CURRENCY_FORMATTERS | 'Abbreviated';

/**
 *
 * @param value A number to return in currency format
 * @param options An Intl.NumberFormatOptions object or predefined format option. If providing an object, the `style` (non-overridable) and `currency` (overridable) keys default to 'currency' and 'USD', respectively
 * @returns The provided number converted to USD currency
 */
export function formatCurrency(
  value: number | string | Nullish,
  options: Intl.NumberFormatOptions | CurrencyFormatOption = 'ToNearestCent',
) {
  if (isNullish(value)) {
    return '';
  }

  const parsedValue = typeof value === 'string' ? Number(value) : value;
  if (isNaN(parsedValue)) {
    return '';
  }

  if (options === 'Abbreviated') {
    return formatAbbreviated(parsedValue);
  }

  const formatter = typeof options === 'string' ? CURRENCY_FORMATTERS[options] : null;

  if (formatter) {
    const formattedValue = formatter.format(parsedValue);
    if (options === 'ToNearestDollar') {
      return formattedValue.split('.')[0];
    }
    return formattedValue;
  }

  if (!!options && typeof options === 'object') {
    // allow `currency: 'USD'` to be overridable and ensure `style: 'currency'` is enforced
    return value.toLocaleString(EN_US, { currency: 'USD', ...options, style: 'currency' });
  }

  return CURRENCY_FORMATTERS.ToNearestCent.format(parsedValue);
}

type FormatCurrencyParams = Parameters<typeof formatCurrency>;

/**
 *
 * @param value A number in cents to return in currency format
 * @param options An Intl.NumberFormatOptions object or predefined format option. If providing an object, the `style` (non-overridable) and `currency` (overridable) keys default to 'currency' and 'USD', respectively
 * @returns The provided cents divided by 100 and converted to USD currency
 */
export function formatCurrencyFromCents(
  value: FormatCurrencyParams[0],
  options?: FormatCurrencyParams[1],
) {
  if (isNullish(value) || value === '') {
    return '';
  }

  return formatCurrency(+value / 100, options);
}
